/**
 * Flow C · অপ্রাপ্য এআই.
 *
 * C1 · আপনার অনুরোধগুলো — `/ai`
 * C2 · কথোপকথন — `/ai/:conversationId`
 * C3 · তুলনা — `/ai/:id/compare`
 * C4 · পরিকল্পনা — `/ai/:id/plan`
 * C5 · বাজেট অনুমান — `/ai/:id/budget`
 *
 * Frames: `Oprappo P0 Screens.dc.html`, "C1 · এআই শুরু" through "C5 · বাজেট অনুমান".
 * The manifest names C1 `আপনার অনুরোধগুলো`, which is the frame's own heading; the frame
 * label still reads "এআই শুরু" from before audit finding 01 moved the door to search.
 * The heading, not the frame label, is what the screen says.
 *
 * C1 IS NOT THE DOOR FOR A NEW REQUEST. It is where ongoing and past requests live —
 * the lede says so ("সার্চে পুরো কথা লিখলে এখানেই চলে আসবেন"), and nothing here starts a
 * new search. The composer the frame draws is transcribed as drawn and flagged in the
 * report as the one place the frame still reads like an entry point.
 *
 * The understood intent renders above the answer in C2 with the `না, অন্য কিছু`
 * correction control (finding 07), using `INTENT_CORRECTION_LABEL_BN` from
 * @oprappo/contracts so the label cannot drift from the contract.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon, IconButton } from '@oprappo/ui';
import { INTENT_CORRECTION_LABEL_BN, formatTaka } from '@oprappo/contracts';
import type {
  ConversationBudget,
  ConversationCompare,
  ConversationDetail,
  ConversationListResponse,
  ConversationPlan,
} from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../onboarding/frame.js';
import { Glyph } from '../onboarding/glyphs.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import {
  fetchBudget,
  fetchCompare,
  fetchConversation,
  fetchConversations,
  fetchPlan,
} from './api.js';
import { AI_HEADER_BN, C1_COPY, C2_COPY, EXAMPLES } from './data.js';

/**
 * ── EVERY SCREEN IS A VIEW PLUS A CONTAINER, AND `data` MAY BE null ──────────
 * The view takes what the route returned and renders it; the container below each one
 * does the loading. That split is what lets the fidelity tests render these screens from
 * the same `fixtures/api/ai.*.json` files the API's own suite asserts its responses
 * against, so the chain the visual gate depends on is checked end to end: drawn frame ==
 * fixture == API output == this markup. Flow F states the same rule at `F1Account.tsx`.
 *
 * `null` means "nothing has arrived yet", and a view renders its chrome and no content
 * rather than substituting design data. That is the whole reason the drawn values left
 * `data.ts`: a constant rendered while a fetch is in flight is a constant rendered when
 * the fetch fails, and then the screen is confidently wrong instead of visibly empty.
 */

/**
 * The 42px teal send square drawn on both composers:
 * `width:42px;height:42px;background:#0E3A45;display:flex;align-items:center;justify-content:center`
 * with a 19px white send glyph.
 *
 * The fill is on the wrapper, not passed to `IconButton` as a class. `IconButton`'s own
 * `bg-transparent` is emitted after `bg-teal` in the preset's palette order, so a
 * `className="bg-teal"` would silently lose the cascade and render a transparent square.
 * That is a general hazard with `cx`, which joins classes without resolving conflicts.
 */
function SendButton(): JSX.Element {
  return (
    <div className="w-[42px] h-[42px] bg-teal flex items-center justify-center flex-none">
      <IconButton control="send" size={19} stroke="#ffffff" />
    </div>
  );
}

/* ---------------------------------------------------- C1 · your requests --- */

export function AiIndexView({ data }: { data: ConversationListResponse | null }): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={SAND_FRAME}>
      <div className="bg-teal p-[16px]">
        <div className="flex gap-[11px] items-center">
          <Icon name="ai" size={24} stroke="#E8A33D" className="flex-none" />
          <div className="font-anek font-semibold text-[18px] leading-[1.75] text-white">
            {AI_HEADER_BN}
          </div>
        </div>
      </div>

      <div className="px-[16px] pt-[22px] flex-1 min-h-0 overflow-y-auto">
        {/* 25px is in the display band: leading 1.25, as drawn. */}
        <h1 className="m-0 font-anek font-semibold text-[25px] leading-[1.25] text-ink">
          {C1_COPY.title_bn}
        </h1>
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[9px]">
          {C1_COPY.lede_bn}
        </div>

        <div className="font-anek font-semibold text-[14px] leading-[1.75] text-muted mt-[22px] mb-[11px]">
          {C1_COPY.ongoing_heading_bn}
        </div>
        {(data?.ongoing ?? []).map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => navigate(`/ai/${c.id}`)}
            className="w-full bg-white border border-border border-l-[3px] border-l-amber px-[14px] py-[13px] flex justify-between items-center gap-[10px] text-left rounded-none"
          >
            <span className="flex-1">
              <span className="block font-anek font-medium text-[14.5px] leading-[1.75] text-ink">
                {c.query_bn}
              </span>
              <span className="block font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[2px]">
                {c.detail_bn}
              </span>
            </span>
            <Glyph name="rowChevron" size={15} stroke="#5B6B69" className="flex-none" />
          </button>
        ))}

        <div className="font-anek font-semibold text-[14px] leading-[1.75] text-muted mt-[20px] mb-[11px]">
          {C1_COPY.examples_heading_bn}
        </div>
        <div className="flex flex-col gap-[10px]">
          {EXAMPLES.map((e) => (
            <div
              key={e}
              className="bg-white border border-border p-[14px] font-anek font-normal text-[15px] leading-[1.75] text-ink2"
            >
              {e}
            </div>
          ))}
        </div>
      </div>

      {/*
        Drawn as a composer. Audit finding 01 made search the one door, so nothing here
        routes a new request; the field is inert and the tension is reported rather than
        resolved by deleting a control the frame draws. C1 also draws no tab bar, unlike
        B1 and B5 — also reported, also transcribed as drawn.
      */}
      <div className="bg-white border-t border-border px-[16px] py-[11px] flex gap-[10px] items-center">
        <div className="flex-1 border border-border h-[46px] flex items-center px-[13px] font-anek font-normal text-[15px] leading-[1.75] text-muted">
          {C1_COPY.composer_placeholder_bn}
        </div>
        <IconButton
          control="voiceSearch"
          size={20}
          onClick={() => navigate('/search/voice')}
          className="flex-none"
        />
        <SendButton />
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------- C2 · conversation --- */

export function ConversationView({ data }: { data: ConversationDetail | null }): JSX.Element {
  const navigate = useNavigate();
  // The customer's sentence is the FIRST user turn and the answer is the LAST assistant
  // turn. Not `turns[0]` and `turns[1]`: a thread grows, and C2 draws the question that
  // started it above the answer that currently stands.
  const asked = data?.turns.find((t) => t.role === 'user');
  const answer = data === null ? undefined : [...data.turns].reverse().find((t) => t.role === 'assistant');
  const result = data?.result;

  return (
    <Frame className={SAND_FRAME}>
      <div className="bg-white border-b border-border px-[16px] py-[13px] flex gap-[11px] items-center">
        <IconButton control="back" size={20} onClick={() => navigate('/ai')} className="flex-none" />
        <Icon name="ai" size={19} stroke="#8A5A0C" className="flex-none" />
        <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
          {AI_HEADER_BN}
        </div>
      </div>

      <div className="flex-1 min-h-0 p-[16px] flex flex-col gap-[15px] overflow-y-auto">
        <div className="self-end bg-teal px-[14px] py-[12px] max-w-[80%]">
          <div className="font-anek font-normal text-[15px] leading-[1.75] text-white">
            {asked?.body_bn ?? ''}
          </div>
        </div>

        {/*
          Audit finding 07. The understood intent sits above the answer, and the
          correction control sits beside it — catching a mistake and fixing it are the
          same tap. The label comes from the contract, never from this file.
        */}
        {/*
          `box-content` — the frame declares `max-width:92%` alongside `border-left:3px`
          and `padding-left:12px` with no `box-sizing`, so in the design the 92% bounds
          the CONTENT box and the block measures 314.9px. Under Tailwind's global
          `border-box` the same declarations bound the border box, the answer column
          loses those 15px and the paragraph rewraps. Measured, not assumed.
        */}
        <div className="self-start box-content max-w-[92%] border-l-[3px] border-l-amber pl-[12px]">
          <div className="flex justify-between items-baseline gap-[10px]">
            <div className="font-anek font-medium text-[13px] leading-[1.75] text-muted">
              {data?.intent.prefix_bn ?? ''}
              <span className="text-ink">{data?.intent.intent_bn ?? ''}</span>
            </div>
            <button
              type="button"
              className="flex-none font-anek font-medium text-[13px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none"
            >
              {INTENT_CORRECTION_LABEL_BN}
            </button>
          </div>
          <div className="font-anek font-normal text-[15px] leading-[1.75] text-ink2 mt-[6px]">
            {answer?.body_bn ?? ''}
          </div>
        </div>

        {/*
          The answer resolves to one bookable card inside the thread — WHEN it resolves
          to one. A thread the AI has just opened has no catalogue behind it and carries
          no result, and then no card is drawn at all: not an empty one, not ৳ 0, not a
          dash. See `apps/api/src/conversations/copy.ts § ARTIFACT_MISSING_COPY_NEEDS`.
        */}
        {result === undefined || data === null ? null : (
        <div className="bg-white border border-border border-t-[3px] border-t-amber p-[13px]">
          <div className="flex justify-between items-start gap-[10px]">
            <div className="flex-1">
              <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                {result.name_bn}
              </div>
              <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                {result.meta_bn}
              </div>
            </div>
            <div className="text-right flex-none">
              <div className="font-manrope font-semibold text-[16px] leading-[1] text-ink [font-variant-numeric:tabular-nums]">
                {formatTaka(result.amount_minor)}
              </div>
              <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[3px]">
                {result.amount_label_bn}
              </div>
            </div>
          </div>
          <div className="flex gap-[9px] mt-[12px] pt-[11px] border-t border-border">
            <button
              type="button"
              onClick={() => navigate(`/offering/${result.id}`)}
              className="bg-teal px-[16px] py-[11px] font-anek font-semibold text-[14px] leading-[1.75] text-white border-0 rounded-none"
            >
              {result.primary_action_bn}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/ai/${data.id}/compare`)}
              className="border border-border px-[15px] py-[11px] font-anek font-semibold text-[14px] leading-[1.75] text-teal bg-transparent rounded-none"
            >
              {result.secondary_action_bn}
            </button>
          </div>
        </div>
        )}

        <div className="self-start flex flex-wrap gap-[8px]">
          {(data?.follow_ups?.chips_bn ?? []).map((f) => (
            <button
              key={f}
              type="button"
              className="bg-white border border-border rounded-pill px-[14px] py-[9px] font-anek font-medium text-[14px] leading-[1.75] text-ink2"
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border-t border-border px-[16px] py-[11px] flex gap-[10px] items-center">
        {/* `box-content` — `height:46px` + `border:1px` is 48px outer as drawn. */}
        <div className="flex-1 box-content border border-border h-[46px] flex items-center px-[13px] font-anek font-normal text-[15px] leading-[1.75] text-muted">
          {C2_COPY.composer_placeholder_bn}
        </div>
        <SendButton />
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------ C3 · compare --- */

export function CompareView({
  id,
  data,
}: {
  id: string;
  data: ConversationCompare | null;
}): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={`${SAND_FRAME} p-[16px] overflow-y-auto`}>
      <div className="flex gap-[12px] items-center mb-[16px]">
        <IconButton control="back" size={20} onClick={() => navigate(`/ai/${id}`)} className="flex-none" />
        <h1 className="m-0 font-anek font-semibold text-[19px] leading-[1.75] text-ink">
          {data?.title_bn ?? ''}
        </h1>
      </div>

      <div className="border-l-[3px] border-l-amber pl-[12px] mb-[18px]">
        <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">
          {data?.rationale_bn ?? ''}
        </div>
      </div>

      <div className="flex flex-col gap-[11px]">
        {(data?.rows ?? []).map((r) => (
          <div
            key={r.id}
            className={
              'bg-white p-[14px] ' +
              (r.lead ? 'border-[1.5px] border-teal' : 'border border-border')
            }
          >
            <div className="flex justify-between items-start gap-[10px]">
              <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                {r.name_bn}
              </div>
              {r.badge_bn !== null ? (
                <div
                  className={
                    'px-[8px] py-[3px] font-anek font-medium text-[13px] leading-[1.75] flex-none ' +
                    (r.badge_tone === 'filled'
                      ? 'bg-teal text-white'
                      : 'border border-border text-body')
                  }
                >
                  {r.badge_bn}
                </div>
              ) : null}
            </div>
            {/* Same three criteria in the same three positions on every card. */}
            <div className="flex justify-between mt-[13px]">
              <div>
                <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
                  {data?.fee_label_bn ?? ''}
                </div>
                <div className="font-manrope font-semibold text-[16px] leading-[1] text-ink [font-variant-numeric:tabular-nums] mt-[4px]">
                  {formatTaka(r.fee_minor)}
                </div>
              </div>
              <div>
                <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
                  {data?.time_label_bn ?? ''}
                </div>
                <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink mt-[4px]">
                  {r.time_bn}
                </div>
              </div>
              <div>
                <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
                  {data?.rating_label_bn ?? ''}
                </div>
                <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink mt-[4px]">
                  {r.rating_bn}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex-1" />

      {/* The one cost that cannot be known yet, stated rather than shown as ৳ 0. */}
      <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">
        {data?.footnote_bn ?? ''}
      </div>
    </Frame>
  );
}

/* --------------------------------------------------------------- C4 · plan --- */

export function PlanView({ id, data }: { id: string; data: ConversationPlan | null }): JSX.Element {
  const navigate = useNavigate();
  const lines = data?.lines ?? [];

  return (
    <Frame className={SAND_FRAME}>
      <div className="p-[16px] border-b border-border bg-white">
        <div className="flex gap-[12px] items-start">
          <IconButton
            control="back"
            size={20}
            onClick={() => navigate(`/ai/${id}`)}
            className="flex-none mt-[3px]"
          />
          <div className="flex-1">
            <h1 className="m-0 font-anek font-semibold text-[18px] leading-[1.75] text-ink">
              {data?.title_bn ?? ''}
            </h1>
            <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
              {data?.subtitle_bn ?? ''}
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-[14px] pt-[13px] border-t border-border">
          <div>
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
              {data?.estimate_label_bn ?? ''}
            </div>
            {/*
              Latin numerals in Manrope, and the STRING the server sent rather than
              `formatTaka(total_minor)`. C4 and C5 draw `৳ 1,42,000` — Bangladeshi lakh
              grouping — and `formatTaka` groups `en-US` and produces `৳ 142,000`. That is
              a defect in a shared package, recorded in `packages/contracts/src/ai.ts
              § Conversations, rule 2`; until it is fixed the drawn string travels beside
              the number instead of being derived from it.
            */}
            <div className="font-manrope font-bold text-[22px] leading-[1.1] text-ink [font-variant-numeric:tabular-nums] mt-[4px]">
              {data?.total_label_bn ?? ''}
            </div>
          </div>
          <div>
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
              {data?.chosen_label_bn ?? ''}
            </div>
            {/* Bangla numerals: Anek Bangla, 22px, so the display band's 1.25. */}
            <div className="font-anek font-bold text-[22px] leading-[1.25] text-ink mt-[4px]">
              {data?.chosen_value_bn ?? ''}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-[16px] overflow-y-auto">
        {lines.map((line, i) => (
          <div
            key={line.id}
            className={
              'py-[13px] flex gap-[12px] items-center' +
              (i === lines.length - 1 ? '' : ' border-b border-border') +
              (line.selected ? '' : ' opacity-[.6]')
            }
          >
            {line.selected ? (
              // `20×20` teal box with a drawn tick: a 9×5 corner rotated −45°.
              <div className="w-[20px] h-[20px] bg-teal flex items-center justify-center flex-none">
                <div className="w-[9px] h-[5px] border-l-2 border-b-2 border-white -rotate-45 -mt-[2px]" />
              </div>
            ) : (
              <div className="w-[20px] h-[20px] border-[1.5px] border-border-warm flex-none" />
            )}
            <div className="flex-1">
              <div
                className={
                  'font-anek font-medium text-[15px] leading-[1.75] ' +
                  (line.selected ? 'text-ink' : 'text-body')
                }
              >
                {line.label_bn}
              </div>
              {line.detail_bn !== null ? (
                <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[1px]">
                  {line.detail_bn}
                </div>
              ) : null}
            </div>
            <div
              className={
                'font-manrope text-[15px] leading-[1] [font-variant-numeric:tabular-nums] flex-none ' +
                (line.selected ? 'font-semibold text-ink' : 'font-medium text-muted')
              }
            >
              {formatTaka(line.amount_minor)}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border-t border-border shadow-bottombar px-[16px] py-[13px]">
        <button
          type="button"
          onClick={() => navigate(`/ai/${id}/budget`)}
          className="w-full bg-teal h-[50px] flex items-center justify-center font-anek font-semibold text-[16px] leading-[1.75] text-white border-0 rounded-none"
        >
          {data?.action_bn ?? ''}
        </button>
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------- C5 · budget --- */

export function BudgetView({
  id,
  data,
}: {
  id: string;
  data: ConversationBudget | null;
}): JSX.Element {
  const navigate = useNavigate();
  // The ticked lines, as the SERVER filtered them. C5 used to re-filter C4's constant
  // here, which meant the budget screen and the plan screen could only ever agree
  // because they read the same array — not because anything made them agree.
  const chosen = data?.lines ?? [];

  return (
    <Frame className={`${SAND_FRAME} p-[16px] overflow-y-auto`}>
      <div className="flex gap-[12px] items-center mb-[18px]">
        <IconButton
          control="back"
          size={20}
          onClick={() => navigate(`/ai/${id}/plan`)}
          className="flex-none"
        />
        <h1 className="m-0 font-anek font-semibold text-[19px] leading-[1.75] text-ink">
          {data?.title_bn ?? ''}
        </h1>
      </div>

      <div className="bg-white border border-border p-[15px] mb-[18px]">
        <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
          {data?.selected_label_bn ?? ''}
        </div>
        <div className="font-manrope font-bold text-[28px] leading-[1.15] text-ink [font-variant-numeric:tabular-nums] mt-[6px]">
          {data?.total_amount_label_bn ?? ''}
        </div>
        {/* Bangla numerals (৳ ২,৮৪০) in prose: Anek Bangla, never Manrope. */}
        <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-body mt-[8px]">
          {data?.per_head_bn ?? ''}
        </div>
      </div>

      <div>
        {chosen.map((l) => (
          <div
            key={l.id}
            className="py-[12px] border-t border-border flex justify-between items-baseline"
          >
            <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">
              {l.label_bn}
            </div>
            <div className="font-manrope font-medium text-[14.5px] leading-[1] text-ink [font-variant-numeric:tabular-nums]">
              {formatTaka(l.amount_minor)}
            </div>
          </div>
        ))}
        <div className="py-[13px] border-t-2 border-t-teal border-b-2 border-b-teal flex justify-between items-center">
          <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
            {data?.total_label_bn ?? ''}
          </div>
          <div className="font-manrope font-bold text-[19px] leading-[1] text-ink [font-variant-numeric:tabular-nums]">
            {data?.total_amount_label_bn ?? ''}
          </div>
        </div>
      </div>

      <div className="border-l-[3px] border-l-amber pl-[12px] mt-[18px]">
        <div className="font-anek font-normal text-[14px] leading-[1.75] text-ink2">
          {data?.advice_bn ?? ''}
        </div>
      </div>

      <div className="flex-1" />

      <div className="bg-sand border border-border px-[14px] py-[12px]">
        <div className="font-anek font-medium text-[14px] leading-[1.75] text-ink">
          {data?.disclaimer_title_bn ?? ''}
        </div>
        <div className="font-anek font-normal text-[13px] leading-[1.75] text-body mt-[3px]">
          {data?.disclaimer_body_bn ?? ''}
        </div>
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------- containers --- */

/**
 * The five containers.
 *
 * Each does the same three things and nothing else: read the route parameter, load, and
 * wrap the view in the boundary for the এআই column of the applicability matrix. The
 * boundary changes nothing about the healthy render — no state resolves unless an API
 * error body says so — so the fidelity these screens were transcribed to is untouched
 * and the gate still measures what it measured.
 *
 * ── WHAT A FAILURE REACHES, AND THE ONE CASE THAT REACHES NOTHING ────────────
 * `useResource` turns a thrown `ApiError` into the body `SurfaceStates` reads, and the
 * এআই column admits six of the eleven states: নতুন ব্যবহারকারী, লোডিং, ইন্টারনেট নেই,
 * প্রদানকারী অনুপলব্ধ, সেবা সাময়িক বন্ধ and সেবাটি আসছে. So an offline phone reaches the
 * drawn offline frame and a paused service reaches its own, with the server's sentence.
 *
 * The case that reaches nothing is a thread that is not this customer's, or is gone:
 * the route answers `E_PERMISSION`, whose drawn state is TINTED for this column — the
 * design says অনুমতি নেই cannot occur on the AI surface — so the boundary correctly
 * renders the children and the customer sees chrome with no thread in it. That is a gap
 * in the design rather than in this file, it is the same one
 * `docs/bangla-needed/account-activity.md § 1` records for `/activity/:id`, and it is
 * recorded again in `docs/bangla-needed/ai.md § 1` rather than papered over by mapping
 * the refusal onto a state the design excluded.
 *
 * ── A MODEL OUTAGE IS NOT A FAILURE STATE HERE ───────────────────────────────
 * When every provider is exhausted the thread still gets an answer — the scripted intent
 * tree, `apps/api/src/conversations/index.ts § answerFor` — and the response carries
 * `simple_mode: true` with the one drawn sentence `এখন সহজ মোডে চলছে` in
 * `simple_mode_label_bn`. It is a 200 and it renders the ordinary screen, because
 * docs/06's rule is "never an error, never a spinner, never a silent queue". Nothing
 * below branches on it, and that is the point.
 */

export function AiIndexScreen(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchConversations(signal), []);
  const { data, error } = useResource(load, 'ai-conversations');

  return (
    <SurfaceStates surface="ai" error={error ?? undefined}>
      <AiIndexView data={data} />
    </SurfaceStates>
  );
}

export function ConversationScreen(): JSX.Element {
  const { conversationId } = useParams();
  const id = conversationId ?? '';
  const load = useCallback((signal: AbortSignal) => fetchConversation(id, signal), [id]);
  const { data, error } = useResource(load, `ai-conversation:${id}`);

  return (
    <SurfaceStates surface="ai" error={error ?? undefined}>
      <ConversationView data={data} />
    </SurfaceStates>
  );
}

export function CompareScreen(): JSX.Element {
  const { id: routeId } = useParams();
  const id = routeId ?? '';
  const load = useCallback((signal: AbortSignal) => fetchCompare(id, signal), [id]);
  const { data, error } = useResource(load, `ai-compare:${id}`);

  return (
    <SurfaceStates surface="ai" error={error ?? undefined}>
      <CompareView id={id} data={data} />
    </SurfaceStates>
  );
}

export function PlanScreen(): JSX.Element {
  const { id: routeId } = useParams();
  const id = routeId ?? '';
  const load = useCallback((signal: AbortSignal) => fetchPlan(id, signal), [id]);
  const { data, error } = useResource(load, `ai-plan:${id}`);

  return (
    <SurfaceStates surface="ai" error={error ?? undefined}>
      <PlanView id={id} data={data} />
    </SurfaceStates>
  );
}

export function BudgetScreen(): JSX.Element {
  const { id: routeId } = useParams();
  const id = routeId ?? '';
  const load = useCallback((signal: AbortSignal) => fetchBudget(id, signal), [id]);
  const { data, error } = useResource(load, `ai-budget:${id}`);

  return (
    <SurfaceStates surface="ai" error={error ?? undefined}>
      <BudgetView id={id} data={data} />
    </SurfaceStates>
  );
}
